import { useMutation } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { useToast } from "@/components/feedback";
import { TasksApi } from "@/openapi/generated";
import { createAuthenticatedApi } from "@/lib/utils";
import type { CreateTaskInput } from "../types";
import { buildTaskPayload } from "../utils/buildTaskPayload";

export const useCreateTask = () => {
  const accessToken = useAccessToken();
  const toast = useToast();

  const { mutate, mutateAsync, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(TasksApi, accessToken);

      await service.apiTasksPost({
        entityName: input.entityName,
        actionName: input.actionName,
        payload: buildTaskPayload(
          input.selectedIds,
          input.executionParameters,
        ),
        files: input.files,
      });
    },
    onSuccess: (_data, variables) => {
      toast({
        title: "Action queued",
        description: `${formatActionLabel(variables.actionName)} has been queued and will run shortly.`,
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Action failed",
        description:
          error instanceof Error
            ? error.message
            : "Could not queue this action. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return {
    createTask: mutate,
    createTaskAsync: mutateAsync,
    isPending,
    isSuccess,
    isError,
  };
};

function formatActionLabel(actionName: string): string {
  const shortName = actionName.includes(".")
    ? actionName.split(".").pop() ?? actionName
    : actionName;

  return shortName
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^Bulk /, "")
    .trim();
}
